import React, { FC, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useRouter } from 'next/router';
import * as S from './DesktopFilters.styles';
import { Property } from '@/api/types/categories.types';
import { BooleanCollection } from '@/components/Filters/FiltersDrawer.types';
import { DesktopFilterAccordion } from '@/components/Filters/DesktopFilters/DesktopFilterAccordion';
import { useUrlAndHashFromPath } from '@/hooks/useUrlAndHashFromPath';
import { formatBooleanValue } from '@/components/Filters/DesktopFilters/DesktopFilters.helpers';
import { AccordionRef } from '@/components/UI/Accordion/Accordion';
import { QUERY } from '@/constants/common';
import { KeywordsSource } from '@/components/SearchPanel/components/SearchSuggestions.types';
import { SearchResultsPageTestIds } from '@/__e2e__/testIds/search-results-page';

interface Props {
  property: Property;
  collection: BooleanCollection;
}

const options = [
  { label: 'common.no_matter', value: undefined },
  { label: 'common.yes', value: true },
  { label: 'common.no', value: false },
];

export const BooleanDesktopFilter: FC<Props> = ({ property, collection }) => {
  const { t } = useTranslation();
  const router = useRouter();
  const { clearUrl, queryString } = useUrlAndHashFromPath();
  const accordionRef = useRef<AccordionRef>(null);

  const currentValue = collection[property.legacyResourceId];

  function getHref(value: boolean | undefined) {
    const searchParams = new URLSearchParams(queryString);
    const key = `p[${property.legacyResourceId}]`;

    searchParams.delete(QUERY.userId);
    searchParams.set(QUERY.keywordsSource, KeywordsSource.Typewritten);

    if (value !== undefined) {
      searchParams.set(key, String(value));
    } else {
      searchParams.delete(key);
    }

    return `${clearUrl}?${searchParams.toString()}`;
  }

  function handleClear() {
    const href = getHref(undefined);
    router.push(href, undefined, { scroll: false });
  }

  return (
    <DesktopFilterAccordion
      ref={accordionRef}
      title={property.name}
      value={formatBooleanValue(currentValue, t)}
      onClear={handleClear}
      dataTestId={`boolean-property-${property.legacyResourceId}`}
    >
      <S.BooleanOptionsContainer>
        {options.map(option => {
          const isSelected = currentValue === option.value;
          return (
            <S.Chip
              key={option.label}
              appearance={isSelected ? 'primary' : 'gray'}
              isRounded
              href={getHref(option.value)}
              onClick={() => accordionRef.current?.close()}
              dataTestId={SearchResultsPageTestIds.desktopFilters.booleanOption}
            >
              {t(option.label)}
            </S.Chip>
          );
        })}
      </S.BooleanOptionsContainer>
    </DesktopFilterAccordion>
  );
};
